import { Component, inject, OnInit, OnDestroy, signal, computed, effect, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService, SiteService, debouncedServerSearch } from '@card-software/nexus-utilities';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { MenuItem } from 'primeng/api';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { Tag } from 'primeng/tag';

import { EnumService } from '../../../services/enum.service';
import { DispatchInstructionProxyService } from '../../../proxies/inventory/dispatch-instruction.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import {
  DispatchInstruction,
  DispatchInstructionStatus,
  DispatchInstructionType,
} from '../../../models/dispatch-instruction/dispatch-instruction';
import {
  DispatchInstructionFilters,
  DispatchInstructionSearchCommand,
} from '../../../models/dispatch-instruction/dispatch-instruction-search';
import { FabricsByColorGroup } from '../../../models/dispatch-instruction/dispatch-instruction-view';
import {
  DISPATCH_TYPE_LABELS,
  MAX_VISIBLE_MANUFACTURER_LOTS,
  SCHEDULED_DATE_MONTH_NAMES,
} from '../../../models/dispatch-instruction/dispatch-instruction.constants';
import { PRIDE_CHEMICALS_SITE_ID } from '../../../models/site.constants';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-dispatch-instructions',
  templateUrl: './dispatch-instructions.component.html',
  styleUrls: ['./dispatch-instructions.component.scss'],
})
export class DispatchInstructionsComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;

  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly dispatchInstructionProxyService = inject(DispatchInstructionProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);

  private readonly destroy$ = new Subject<void>();
  private appliedFilters: DispatchInstructionFilters = {};

  readonly maxVisibleManufacturerLots = MAX_VISIBLE_MANUFACTURER_LOTS;

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  dispatchInstructions = signal<DispatchInstruction[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'dispatch-instructions-filters', config: this.filterConfig });
  clientSuggestions = signal<Business[]>([]);

  dispatchInstructionStatus = signal<EnumValues>({});
  enumsReady = signal(false);

  // Lot item number → manufacturer lot. The search only returns lot numbers, so the manufacturer lot is
  // looked up once per lot and kept across pages ('' marks a lot that has none).
  private readonly manufacturerLotByLotItem = signal<Map<string, string>>(new Map());

  manufacturerLotsByInstruction = computed(() => {
    const manufacturerLots = this.manufacturerLotByLotItem();
    const result = new Map<string, string[]>();

    for (const instruction of this.dispatchInstructions()) {
      const values = new Set<string>();
      for (const lotItemNumber of this.getLotItemNumbers(instruction)) {
        const manufacturerLot = manufacturerLots.get(lotItemNumber);
        if (manufacturerLot) values.add(manufacturerLot);
      }
      result.set(instruction.id, Array.from(values).sort());
    }

    return result;
  });

  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;
  private numberSortOrder: 0 | 1 | -1 = 0;

  // All create options; the Chemical option is gated by site (Pride Chemicals only).
  private readonly allSpeedDialItems: MenuItem[] = [
    {
      id: 'standard',
      label: 'Standard',
      icon: 'pi pi-file',
      command: () => this.createNewDispatch(DispatchInstructionType.Standard),
    },
    // TODO: Enable B-Grade dispatch creation when the feature is available and fully tested
    {
      id: 'bgrade',
      label: 'B-Grade',
      icon: 'pi pi-tag',
      command: () => this.createNewDispatch(DispatchInstructionType.BGrade),
    },
    {
      id: 'greige',
      label: 'Greige Only',
      icon: 'pi pi-box',
      command: () => this.createNewDispatch(DispatchInstructionType.GreigeOnly),
    },
    {
      id: 'chemical',
      label: 'Chemical',
      icon: 'pi pi-flask',
      command: () => this.createNewDispatch(DispatchInstructionType.Chemical),
    },
  ];

  // SpeedDial items for creating new dispatch instructions, filtered by the selected site:
  // Pride Chemicals shows only Chemical dispatches; Northern Textiles (and any other site) hides Chemical.
  get speedDialItems(): MenuItem[] {
    const isPrideChemicals = SiteService.getSelectedSiteId() === PRIDE_CHEMICALS_SITE_ID;
    return this.allSpeedDialItems.filter((item) => (isPrideChemicals ? item.id === 'chemical' : item.id !== 'chemical'));
  }

  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.dispatchInstructionStatus();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  // Creation is gated by DispatchInstructionCreationAdmin (PBI 4569) — the API now enforces this
  // same role on POST, so this mirrors it to avoid showing an "Add" action that would 403.
  canCreate = computed(() => {
    return AuthService.hasRole(UserGroup.DispatchInstructionCreationAdmin);
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('DispatchInstructionStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.dispatchInstructionStatus.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // The dynamic-filters panel renders the autocomplete, so there is no element for
    // ServerSearchDirective: the shared operator is wired by hand and released with the component.
    this.clientQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchClients(query)),
        takeUntil(this.destroy$),
      )
      .subscribe((clients) => this.clientSuggestions.set(clients));

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('Dispatch_Instruction_Number', 'Dispatch Number'),
          placeholder: this.translate('Dispatch_Enter_Number', 'Enter dispatch number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'invoiceNumber',
          label: this.translate('Dispatch_Invoice_Number', 'Invoice Number'),
          placeholder: this.translate('Dispatch_Enter_Invoice_Number', 'Enter invoice number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'clientReference',
          label: this.translate('Dispatch_Client_Reference', 'Client Reference'),
          placeholder: this.translate('Dispatch_Search_Client', 'Search client...'),
          autocompleteConfig: {
            suggestions: this.clientSuggestions(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.clientQueries$.next(event.query),
            resolveById: (id: string) => this.resolveBusiness(id),
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'clientProductOrderNumber',
          label: this.translate('Dispatch_Client_PO', 'Client Product Order'),
          placeholder: this.translate('Dispatch_Enter_Client_PO', 'Enter client PO number'),
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: this.translate('Dispatch_Status', 'Status'),
          placeholder: this.translate('Dispatch_Select_Status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'priorityDateFrom',
          label: this.translate('Dispatch_Priority_Date_From', 'Priority Date From'),
          placeholder: this.translate('Dispatch_Select_Start_Date', 'Select start date'),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'priorityDateTo',
          label: this.translate('Dispatch_Priority_Date_To', 'Priority Date To'),
          placeholder: this.translate('Dispatch_Select_End_Date', 'Select end date'),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private increaseLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private decreaseLoading(): void {
    this.isLoading.update((val) => Math.max(0, val - 1));
  }

  private readonly clientQueries$ = new Subject<string>();

  private searchClients(query: string): Observable<Business[]> {
    return this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client] })
      .pipe(map((result) => result?.data?.items || []));
  }

  search(): void {
    const formValues = this.appliedFilters;

    const number = formValues.number?.trim();
    const invoiceNumber = formValues.invoiceNumber?.trim();
    const clientReferenceRaw = formValues.clientReference;
    const clientReference = typeof clientReferenceRaw === 'string' ? clientReferenceRaw : clientReferenceRaw?.id;
    const clientProductOrderNumber = formValues.clientProductOrderNumber?.trim();

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? statusesRaw.map((s): DispatchInstructionStatus => Number(typeof s === 'object' && 'value' in s ? s.value : s))
      : [];

    const priorityDateFrom = formValues.priorityDateFrom ? this.formatDateForApi(formValues.priorityDateFrom) : undefined;
    const priorityDateTo = formValues.priorityDateTo ? this.formatDateForApi(formValues.priorityDateTo) : undefined;

    const command: DispatchInstructionSearchCommand = {
      number: number || undefined,
      invoiceNumber: invoiceNumber || undefined,
      clientReference: clientReference || undefined,
      clientProductOrderNumber: clientProductOrderNumber || undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      priorityDateFrom: priorityDateFrom,
      priorityDateTo: priorityDateTo,
    };

    const sortField = this.numberSortOrder !== 0 ? 'number' : undefined;
    const sortOrder = this.numberSortOrder !== 0 ? this.numberSortOrder : -1;

    this.increaseLoading();
    this.dispatchInstructionProxyService
      .search(command, this.currentPage, this.pageSize, sortField, sortOrder)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (result) => {
          const instructions = result?.data?.items || [];
          this.dispatchInstructions.set(instructions);
          this.totalRecords = result?.data?.totalCount || 0;
          this.loadManufacturerLots(instructions);
        },
        error: () => {
          this.dispatchInstructions.set([]);
          this.totalRecords = 0;
        },
      });
  }

  // Doesn't block the table: the column fills in when the lookup returns.
  private loadManufacturerLots(instructions: DispatchInstruction[]): void {
    const known = this.manufacturerLotByLotItem();
    const missing = Array.from(new Set(instructions.flatMap((instruction) => this.getLotItemNumbers(instruction)))).filter(
      (lotItemNumber) => !known.has(lotItemNumber),
    );
    if (missing.length === 0) return;

    this.lotItemProxyService
      .getByLotNumbers(missing)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          const updated = new Map(this.manufacturerLotByLotItem());
          for (const lotItem of data ?? []) {
            if (lotItem.number) updated.set(lotItem.number, lotItem.providerLotId ?? '');
          }
          this.manufacturerLotByLotItem.set(updated);
        },
      });
  }

  private getLotItemNumbers(instruction: DispatchInstruction): string[] {
    return (instruction.chemicalLines ?? []).flatMap((line) => (line.lotItems ?? []).map((lotItem) => lotItem.lotItemNumber));
  }

  getManufacturerLots(instruction: DispatchInstruction): string[] {
    return this.manufacturerLotsByInstruction().get(instruction.id) ?? [];
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters as DispatchInstructionFilters;
    this.currentPage = 1;
    this.search();
  }

  onTableSort(event: TableLazyLoadEvent): void {
    if (!event.sortField || event.sortField !== 'number') return;

    const incomingOrder = (event.sortOrder ?? 1) as 1 | -1;

    if (incomingOrder === 1 && this.numberSortOrder === -1) {
      this.numberSortOrder = 0;
      this.dt.sortField = null;
      this.dt.sortOrder = 1;
      this.dt.tableService.onSort(null);
    } else {
      this.numberSortOrder = incomingOrder;
    }

    this.currentPage = 1;
    this.search();
  }

  redirectToDetail(id?: string, type?: DispatchInstructionType): void {
    const queryParams: Record<string, string | number> = {};
    if (id) queryParams['id'] = id;
    if (type !== undefined) queryParams['type'] = type;
    this.router.navigate(['/dispatch-instruction-details'], { queryParams });
  }

  createNewDispatch(type: DispatchInstructionType): void {
    this.router.navigate(['/dispatch-instruction-details'], {
      queryParams: { type: type },
    });
  }

  getTypeLabel(type?: DispatchInstructionType): string | undefined {
    const label = type !== undefined && type !== null ? DISPATCH_TYPE_LABELS[type] : undefined;
    return label ? this.translate(label.labelKey, label.fallback) : undefined;
  }

  getStatusLabel(status: DispatchInstructionStatus): string {
    const statuses = this.dispatchInstructionStatus();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: DispatchInstructionStatus): Tag['severity'] {
    switch (status) {
      case DispatchInstructionStatus.Draft:
        return 'secondary';
      case DispatchInstructionStatus.SentToWarehouse:
        return 'info';
      case DispatchInstructionStatus.InProgress:
        return 'warn';
      case DispatchInstructionStatus.Completed:
        return 'success';
      case DispatchInstructionStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getRollCount(instruction: DispatchInstruction): number {
    let count = 0;
    for (const line of instruction.lines || []) {
      for (const fabric of line.fabricsAndBalances || []) {
        count += fabric.scannedRolls?.length || 0;
      }
    }
    return count;
  }

  getLineCount(instruction: DispatchInstruction): number {
    return instruction.lines?.length || 0;
  }

  hasFabricsByColor(instruction: DispatchInstruction): boolean {
    if (!instruction.lines || instruction.lines.length === 0) {
      return false;
    }

    for (const line of instruction.lines) {
      if (line.fabricsByColor && Object.keys(line.fabricsByColor).length > 0) {
        return true;
      }
    }

    return false;
  }

  getFabricsByColor(instruction: DispatchInstruction): FabricsByColorGroup[] {
    const fabricsByColorMap = new Map<string, Set<string>>();

    for (const line of instruction.lines || []) {
      if (line.fabricsByColor) {
        for (const colorName in line.fabricsByColor) {
          const fabrics = line.fabricsByColor[colorName];
          if (fabrics && Array.isArray(fabrics)) {
            if (!fabricsByColorMap.has(colorName)) {
              fabricsByColorMap.set(colorName, new Set<string>());
            }
            fabrics.forEach((fabric) => fabricsByColorMap.get(colorName)!.add(fabric));
          }
        }
      }
    }

    return Array.from(fabricsByColorMap.entries())
      .map(([color, fabricsSet]) => ({
        color,
        fabrics: Array.from(fabricsSet).sort(),
      }))
      .sort((a, b) => a.color.localeCompare(b.color));
  }

  truncateAddress(address: string, maxLength = 35): string {
    if (!address) return '';
    if (address.length <= maxLength) return address;
    return address.substring(0, maxLength) + '...';
  }

  formatScheduledDate(dateString?: string): string {
    if (!dateString) return '-';

    try {
      // dateString is a day-only value (YYYY-MM-DD...); parse the calendar parts directly instead of
      // `new Date(dateString)`, which reads it as UTC midnight and can render as the previous day locally.
      const [datePartYear, datePartMonth, datePartDay] = dateString.split('T')[0].split('-').map(Number);
      const date = new Date(datePartYear, datePartMonth - 1, datePartDay);
      const currentLang = this.translateService.currentLang || 'es';

      const months = SCHEDULED_DATE_MONTH_NAMES[currentLang] || SCHEDULED_DATE_MONTH_NAMES['es'];
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch {
      return dateString;
    }
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private translate(key: string, fallback: string, params?: Record<string, unknown>): string {
    const translation = this.translateService.instant(key, params);
    return translation === key ? fallback : translation;
  }
}
